import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/session';
import { useCategorieActive, useDossierActif } from '../dossier/dossierActif';
import { EntreeUrgence, FenetreDepot } from '../dossier/MenuUrgence';
import { CATEGORIES_EN_MENU } from '../dossier/Navigation';
import type { Changements } from '../dossier/Nouveautes';
import { useTraduction } from '../i18n/langue';
import { oublierAppareilCourant } from '../natif/notifications';
import { Boussole, RoseDesVents } from '../support/Boussole';
import { Invisible, Picto } from '../ui';
import { ECRAN_LARGE, useRequeteMedia } from '../ui/media';
import {
  IconeCompte,
  IconeDossiers,
  IconeNouveauDossier,
  IconeOptions,
  IconeSortie,
} from './IconesMenu';

/**
 * Le menu de l'en-tête : tout ce qui n'est pas un écran, derrière une seule porte.
 *
 * L'en-tête n'offrait qu'une commande — l'adresse du compte, sur laquelle on cliquait pour
 * se déconnecter. Une seule action pour tout ce qui touche au compte, et deux réglages
 * (langue, apparence) échoués à côté de la marque faute d'endroit où les mettre.
 *
 * Quatre choix de conception, chacun pour une raison précise :
 *
 * - **Un menu, pas une page de réglages de plus.** Ce qu'on cherche en cliquant sur son
 *   adresse tient en quelques entrées ; les faire traverser un écran intermédiaire pour
 *   choisir sa langue serait deux clics au lieu d'un.
 * - **Un burger sur téléphone, l'adresse sur grand écran.** Le même bouton, le même menu,
 *   deux vêtements : sous 860 px, l'adresse électronique mangeait la moitié de la largeur
 *   du bandeau — sur un écran de 360 px, `prenom.nom@fournisseur.fr` laissait au nom du
 *   produit de quoi écrire « Rep… ». Le trait de burger dit la même chose en 24 px, et
 *   c'est le geste que tout le monde connaît déjà à cet endroit. Le basculement est fait
 *   par la feuille de style, sur un seul balisage : deux composants auraient divergé à la
 *   première entrée ajoutée d'un côté.
 * - **L'urgence est la première entrée, et seulement dans un dossier.** Elle occupait une
 *   barre rouge pleine largeur sous la navigation du téléphone ; elle est ici parce qu'un
 *   geste n'est pas un écran, et en tête parce qu'on ne cherche pas en bas d'une liste ce
 *   qu'on ouvre dans l'urgence. Hors d'un dossier, elle n'a pas de destinataire : elle ne
 *   se rend pas plutôt que de s'afficher inerte.
 * - **La déconnexion reste écrite en toutes lettres, et en dernier.** Elle était la seule
 *   action de l'en-tête ; la noyer au milieu du menu la rendrait facile à toucher par
 *   erreur, et se déconnecter par surprise au milieu d'une saisie n'est pas rattrapable.
 * - **Le menu se ferme à l'`Échap` et au clic extérieur.** Un menu qui ne se ferme qu'en
 *   rappuyant sur son bouton reste ouvert par-dessus l'écran qu'on venait consulter.
 *
 * Deux groupes, séparés par un trait : ce qui parle du **dossier ouvert** — urgence, retour
 * à la liste, enfants — puis ce qui parle du **compte**. « Mes dossiers » a rejoint le
 * premier groupe en quittant l'en-tête du dossier, où il tenait sa propre ligne pour trois
 * mots ; il reste juste après l'urgence, l'autre sortie du dossier qu'on regarde.
 */
export function MenuCompte() {
  const { client, profil, deconnecter } = useSession();
  const { messages } = useTraduction();
  const m = messages.compte;
  const naviguer = useNavigate();
  const dossierActif = useDossierActif();
  const [ouvert, setOuvert] = useState(false);
  // La fenêtre de dépôt est tenue ici, et non dans l'entrée du menu : choisir « Urgence »
  // referme le panneau, et une fenêtre logée dans le panneau serait démontée par le geste
  // même qui l'ouvre. L'aide suit la même règle, pour la même raison.
  const [urgence, setUrgence] = useState(false);
  const [aide, setAide] = useState(false);
  const bloc = useRef<HTMLDivElement>(null);

  /**
   * Les deux catégories que la barre du bas ne porte plus sur téléphone.
   *
   * Elles ne se rendent que là où elles manquent : sur grand écran, le rail les affiche
   * déjà, et les répéter ici ferait deux chemins vers la même page dans le même écran.
   *
   * Leurs pastilles voyagent avec elles. Une catégorie rangée dans un menu fermé est une
   * catégorie dont on ne voit plus les nouveautés — c'est le prix habituel de ce genre de
   * déplacement, et il se paie en documents déposés que personne ne remarque. Le compte
   * suit donc l'entrée, et le burger porte un point tant qu'une des deux a quelque chose
   * à dire. Même clé que l'écran du dossier : aucune requête de plus.
   */
  const categorieActive = useCategorieActive();
  const rail = useRequeteMedia(ECRAN_LARGE);
  const categoriesDeplacees = dossierActif && !rail ? CATEGORIES_EN_MENU : [];

  const changements = useQuery({
    queryKey: ['changes', dossierActif],
    queryFn: () => client.request<Changements>(`/case-files/${dossierActif}/changes`),
    enabled: categoriesDeplacees.length > 0,
  });
  const parCategorie = changements.data?.parCategorie ?? {};
  const nouveautesCachees = categoriesDeplacees.reduce(
    (total, categorie) => total + (parCategorie[categorie] ?? 0),
    0,
  );

  useEffect(() => {
    if (!ouvert) return;

    const auClic = (evenement: MouseEvent) => {
      if (!bloc.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  if (!profil) return null;

  return (
    <div className="menu-compte" ref={bloc}>
      <button
        type="button"
        className="compte"
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label={m.menu}
        onClick={() => setOuvert((etat) => !etat)}
      >
        {/* Trois traits, et non un caractère « ☰ » : la police du système le rend
            différemment d'un appareil à l'autre, parfois en emoji coloré. Trois `<span>`
            se peignent à l'identique partout et se transforment en croix à l'ouverture,
            ce qu'un glyphe ne sait pas faire. */}
        <span className="menu-burger" aria-hidden="true">
          <span />
          <span />
          <span />
          {/* Le point dit qu'il y a du nouveau **derrière cette porte**, pas ailleurs :
              il ne paraît que si une catégorie rangée dans le menu a quelque chose à
              annoncer. Sans lui, ranger les enfants ici reviendrait à éteindre sa
              pastille. */}
          {nouveautesCachees > 0 ? <span className="menu-burger-point" /> : null}
        </span>
        {nouveautesCachees > 0 ? (
          <Invisible>{messages.dossier.navigation.nouveaute(nouveautesCachees)}</Invisible>
        ) : null}
        <span className="compte-identite">{profil.email}</span>
        <span className="compte-chevron" aria-hidden="true">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      {ouvert ? (
        <div className="menu-compte-liste" role="menu">
          {dossierActif ? (
            <EntreeUrgence
              surChoix={() => {
                setOuvert(false);
                setUrgence(true);
              }}
            />
          ) : null}
          {/* Le retour à la liste des dossiers, qui vivait en tête de l'en-tête du
              dossier. Juste après l'urgence : les deux sont des sorties du dossier
              qu'on regarde, l'une vers un geste, l'autre vers l'écran d'où l'on vient.
              N'apparaît que **dans** un dossier — sur l'accueil, on y est déjà. */}
          {dossierActif ? (
            <Link
              to="/"
              role="menuitem"
              className="menu-compte-entree"
              onClick={() => setOuvert(false)}
            >
              <span className="menu-compte-picto" aria-hidden="true">
                <IconeDossiers />
              </span>
              {m.mesDossiers}
            </Link>
          ) : null}
          {/* Les enfants, chassés de la barre du bas par les 60 px que six onglets
              laissaient à chacun — le journal, lui, y est revenu. Elle garde son
              pictogramme : c'est à lui qu'on la reconnaît d'un écran à l'autre, et le
              rail de l'écran large le montre toujours. `aria-current` dit qu'on y est
              déjà — le seul repère qui reste, maintenant qu'aucun onglet de la barre ne
              peut s'allumer pour elle. */}
          {categoriesDeplacees.map((categorie) => {
            const compte = parCategorie[categorie] ?? 0;
            const ici = categorie === categorieActive;
            return (
              <button
                key={categorie}
                type="button"
                role="menuitem"
                aria-current={ici ? 'page' : undefined}
                className={ici ? 'menu-compte-entree ici' : 'menu-compte-entree'}
                onClick={() => {
                  setOuvert(false);
                  void naviguer({
                    to: '/dossiers/$dossierId',
                    params: { dossierId: dossierActif as string },
                    search: { vue: categorie },
                  });
                }}
              >
                <span className="menu-compte-picto" aria-hidden="true">
                  <Picto categorie={categorie} />
                </span>
                {messages.dossier.categories[categorie]}
                {compte > 0 ? (
                  <>
                    <span className="menu-compte-compte" aria-hidden="true">
                      {compte > 99 ? '99+' : compte}
                    </span>
                    <Invisible>{messages.dossier.navigation.nouveaute(compte)}</Invisible>
                  </>
                ) : null}
              </button>
            );
          })}
          {/* Ouvrir un dossier, et nulle part ailleurs.
              L'action tenait un bouton permanent sous la liste de l'accueil : on ouvre un
              dossier une ou deux fois dans sa vie, et il occupait l'écran qu'on consulte
              tous les jours au même rang que les gestes quotidiens. Ici, elle est là où
              l'on va chercher ce qu'on fait rarement — et elle reste joignable de
              partout, y compris depuis l'intérieur d'un dossier, ce que le bouton de
              l'accueil ne permettait pas.

              Elle se rend toujours, même sans aucun dossier : un menu dont les entrées
              apparaissent et disparaissent s'apprend deux fois. À la première venue,
              l'accueil porte de toute façon son propre appel, en grand. */}
          <Link
            to="/dossiers/creer"
            role="menuitem"
            className="menu-compte-entree"
            onClick={() => setOuvert(false)}
          >
            <span className="menu-compte-picto" aria-hidden="true">
              <IconeNouveauDossier />
            </span>
            {m.nouveauDossier}
          </Link>
          {/* Le trait sépare ce qui parle des dossiers (urgence, mes dossiers, enfants,
              nouveau dossier) de ce qui parle du compte — deux registres qui ne se rangent
              pas dans le même tiroir. Il se dessine désormais toujours : « Nouveau
              dossier » est rendu dans tous les cas, donc le premier groupe n'est jamais
              vide et la ligne sépare toujours quelque chose. */}
          <Link
            to="/compte"
            role="menuitem"
            className="menu-compte-entree debut-groupe"
            onClick={() => setOuvert(false)}
          >
            <span className="menu-compte-picto" aria-hidden="true">
              <IconeCompte />
            </span>
            {m.monCompte}
          </Link>
          <Link
            to="/options"
            role="menuitem"
            className="menu-compte-entree"
            onClick={() => setOuvert(false)}
          >
            <span className="menu-compte-picto" aria-hidden="true">
              <IconeOptions />
            </span>
            {m.options}
          </Link>
          {/* L'aide, là où elle ne gêne plus. Elle flottait en pastille au-dessus du coin
              bas droit de chaque écran : elle y recouvrait la fin des cartes de l'accueil
              et venait se poser sur la barre des catégories du dossier, c'est-à-dire sur
              la seule rangée de commandes qu'on vise au pouce. Une question sur le
              calendrier se pose toujours depuis les dépenses — elle reste donc dans la
              coquille, joignable de partout, mais derrière la porte des gestes.
              Son icône est celle de la fenêtre qu'elle ouvre (`RoseDesVents`) : deux
              dessins pour une seule porte auraient fini par diverger. */}
          <button
            type="button"
            role="menuitem"
            className="menu-compte-entree"
            onClick={() => {
              setOuvert(false);
              setAide(true);
            }}
          >
            <span className="menu-compte-picto" aria-hidden="true">
              <RoseDesVents />
            </span>
            {m.aide}
          </button>
          {/* La déconnexion emmène à la connexion, elle ne se contente pas de vider la
              session. Sans cette navigation, l'accueil se rendait une dernière fois sans
              profil — le temps que la garde de route s'exécute — et proposait de se
              reconnecter dans un encart, juste avant de partir.
              L'appareil est détaché **avant** : après, il n'y a plus de jeton pour
              autoriser le détachement, et un téléphone prêté continuerait de recevoir les
              nouveautés du compte qui vient d'en sortir. Sans coque mobile, l'appel ne
              fait rien. */}
          <button
            type="button"
            role="menuitem"
            className="menu-compte-entree sortie"
            onClick={() => {
              setOuvert(false);
              void oublierAppareilCourant(client)
                .catch(() => undefined)
                .then(() => deconnecter())
                .then(() => naviguer({ to: '/connexion' }));
            }}
          >
            <span className="menu-compte-picto" aria-hidden="true">
              <IconeSortie />
            </span>
            {messages.coquille.deconnexion}
          </button>
        </div>
      ) : null}

      {urgence && dossierActif ? (
        <FenetreDepot caseFileId={dossierActif} onFermer={() => setUrgence(false)} />
      ) : null}

      {aide ? <Boussole onFermer={() => setAide(false)} /> : null}
    </div>
  );
}
